import { useFormikContext, Form as FormikForm, FormikErrors, FormikValues } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import useLocaleCode from '../../hooks/useLocaleCode'
import useTranslate from '../../hooks/useTranslate'
import { Icon, renderIconSVG } from '../../utils'
import sendInputMetrics from './utils/sendInputMetrics'
import Field from './Field'
import { IFormField } from './@types/FormField'
import classNames from 'classnames'

interface IFormContent {
  icon?: Icon
  submitButtonLabel: string
  disableSubmitOnEmptyForm?: boolean
  customErrorMessage?: string
  showResetButton?: boolean
  theme?: 'default' | 'marketo' | 'rounded'
  fields: IFormField[]
}

export const testIds = {
  form: 'test-form-element',
  heading: 'test-form-element-heading',
  submitButton: 'test-form-element-submit-button',
  resetButton: 'test-form-element-reset-button',
}
export const submitButtonSubmittingText = 'Please wait...'

export const FormContent = ({
  icon,
  submitButtonLabel,
  disableSubmitOnEmptyForm,
  customErrorMessage,
  showResetButton,
  theme,
  fields,
}: IFormContent): React.ReactElement => {
  const [changed, setChanged] = useState<Record<string, boolean>>({})

  const hasError = (errors: FormikErrors<FormikValues>, fieldId: string) => !!errors[fieldId]
  const localeCode = useLocaleCode()
  const { translate } = useTranslate()

  const [forceValidation, setForceValidation] = useState<boolean>(false)
  const [forceUpdate, setForceUpdate] = useState<number>(0)
  const [noErrors, setNoErrors] = useState<boolean>(false)

  const setFieldChanged = (field: string) => {
    const newChanged = Object.assign({}, changed, { [field]: true })
    setChanged(newChanged)
  }
  const { errors, isSubmitting, handleChange, resetForm, values, setFieldTouched, dirty } =
    useFormikContext<Record<string, any>>()
  const filteredFields = fields.filter((field) => field.type != 'hidden')
  const onChange = useCallback((event: React.ChangeEvent<any>) => {
    const fieldId = event.target.name

    if (values[fieldId] !== event.target.value) {
      setFieldChanged(fieldId)
      setFieldTouched(
        fieldId,
        true, // isTouched
        true, // shouldValidate
      )
      handleChange(event)
    }
  }, [])

  const iconElement = () => {
    if (icon) return renderIconSVG(icon, 'submit-button-icon')
    if (theme === 'rounded') return renderIconSVG(Icon.Arrow, 'submit-button-arrow')
  }

  const submitButtonClassnames = classNames('submit-button', {
    'submit-button--active': noErrors,
  })

  useEffect(() => {
    if (dirty && errors && Object.keys(errors).length === 0) {
      setNoErrors(true)
    } else {
      setNoErrors(false)
    }
  }, [errors, dirty])

  return (
    <FormikForm className="form" autoComplete="on">
      {fields.map((field) => (
        <Field
          {...field}
          hasError={hasError(errors, field.id)}
          customErrorMessage={customErrorMessage}
          onChange={onChange}
          isDisabled={isSubmitting}
          key={`${field.id}-${forceUpdate}`}
          enableValidation={!!changed[field.id] || forceValidation}
          localeCode={localeCode}
        />
      ))}

      <div className="button-wrapper">
        <button
          className={submitButtonClassnames}
          type="submit"
          disabled={isSubmitting || (disableSubmitOnEmptyForm && !dirty)}
          data-testid={testIds.submitButton}
          onClick={() => {
            setForceValidation(true)
            if (errors && Object.keys(errors)?.length) sendInputMetrics(errors, filteredFields)
          }}
        >
          {isSubmitting ? (
            translate(submitButtonSubmittingText)
          ) : (
            <>
              {translate(submitButtonLabel)}
              {iconElement()}
            </>
          )}
        </button>

        {showResetButton && (
          <button
            type="reset"
            className="reset-button"
            onClick={() => {
              resetForm()
              setChanged({})
              setForceValidation(false)
              setForceUpdate(forceUpdate + 1)
            }}
            data-testid={testIds.resetButton}
          >
            {translate('Reset')}
          </button>
        )}
      </div>
    </FormikForm>
  )
}
